import { useState, useEffect } from 'react';
import {
  Megaphone, Globe, Sparkles, Shield, ShieldOff, Loader2, AlertTriangle,
  CheckCircle, XCircle, ExternalLink, Eye, EyeOff, Settings, Hash,
  TrendingUp, BarChart3, Coins, Trophy, Bell, BookOpen, ChevronDown,
  ChevronUp, Info,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  getBroadcastChannels, getBroadcastMyPrefs, setBroadcastPrefs,
  getBroadcastAdminConfig, setBroadcastAdminConfig,
} from '../services/api';
import { InfoTip } from '../components/ui';

const CHANNEL_ICON = {
  'smart-trade-fires':  TrendingUp,
  'grid-bot-action':    BarChart3,
  'dca-accumulation':   Coins,
  'big-wins':           Trophy,
  'alerts':             Bell,
};

const CHANNEL_ACCENT = {
  'smart-trade-fires':  'text-amber-400 border-amber-500/30 bg-amber-500/5',
  'grid-bot-action':    'text-emerald-400 border-emerald-500/30 bg-emerald-500/5',
  'dca-accumulation':   'text-violet-400 border-violet-500/30 bg-violet-500/5',
  'big-wins':           'text-yellow-400 border-yellow-500/30 bg-yellow-500/5',
  'alerts':             'text-pink-400 border-pink-500/30 bg-pink-500/5',
};

export default function OfficialChannelsPage() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [excludeLosses, setExcludeLosses] = useState(false);
  const [minTradeUsd, setMinTradeUsd] = useState('0');
  const [showAdmin, setShowAdmin] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(true);
  const [prefsError, setPrefsError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [chRes, prefsRes] = await Promise.all([
        getBroadcastChannels(),
        getBroadcastMyPrefs(),
      ]);
      setChannels(chRes?.data?.channels || []);
      const p = prefsRes?.data;
      setPrefs(p);
      if (p?.displayName) setDisplayName(p.displayName);
      if (p?.filters) {
        setExcludeLosses(!!p.filters.excludeLosses);
        setMinTradeUsd(String(p.filters.minTradeUsd || 0));
      }
    } catch {} finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function handleSavePrefs(nextOptIn) {
    setSaving(true);
    setPrefsError(null);
    try {
      await setBroadcastPrefs({
        enabled: nextOptIn != null ? nextOptIn : !!prefs?.optedIn,
        displayName: displayName || null,
        filters: {
          excludeLosses,
          minTradeUsd: parseFloat(minTradeUsd) || 0,
        },
      });
      await load();
    } catch (err) {
      // Inline error banner instead of blocking alert().
      setPrefsError(err?.message || 'Could not save your broadcast preferences.');
    } finally { setSaving(false); }
  }

  const isAdmin = user?.role === 'admin';
  const optedIn = !!prefs?.optedIn;
  const liveChannelCount = channels.filter(c => c.configured).length;

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      {/* ─── Hero ─────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl border border-pink-500/20 bg-gradient-to-br from-pink-500/10 via-violet-500/5 to-dark-800/40 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-pink-400/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-violet-500 flex items-center justify-center shadow-lg shadow-pink-500/20">
                <Megaphone className="w-5 h-5 text-dark-900" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Official Channels</h1>
                <p className="text-xs text-pink-300/80 mt-0.5 flex items-center gap-1.5">
                  <Globe className="w-3 h-3" /> Broadcast your real bot events to the haythix-owned Discord + Telegram community
                  <InfoTip side="bottom">Privacy: only display name + tier broadcast — wallet / balance / exchange identity NEVER. Failed broadcasts silently drop (no retry, no block on bot engines). For YOUR own Discord, use Outbound Webhooks instead.</InfoTip>
                </p>
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowAdmin(!showAdmin)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs"
              >
                <Settings className="w-3 h-3" /> {showAdmin ? 'Hide admin' : 'Admin: channel URLs'}
              </button>
            )}
          </div>

          {/* Stats band */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <HeroStat
              icon={Hash}
              label="Channels"
              value={`${liveChannelCount}/${channels.length}`}
              sub={liveChannelCount > 0 ? `${liveChannelCount} live` : 'awaiting setup'}
              accent="text-pink-400"
            />
            <HeroStat
              icon={Eye}
              label="Your status"
              value={optedIn ? 'Broadcasting' : 'Private'}
              accent={optedIn ? 'text-emerald-400' : 'text-gray-400'}
            />
            <HeroStat
              icon={Sparkles}
              label="Display name"
              value={prefs?.displayName || '—'}
              accent="text-violet-400"
            />
            <HeroStat
              icon={Shield}
              label="Filter"
              value={excludeLosses ? 'Wins only' : minTradeUsd > 0 ? `≥$${minTradeUsd}` : 'All events'}
              accent="text-amber-400"
            />
          </div>
        </div>
      </div>

      {/* ─── What is this tool? ──────────────────────────────────────────── */}
      <AboutCard open={aboutOpen} onToggle={() => setAboutOpen(o => !o)} />

      {/* ─── Admin panel ──────────────────────────────────────────────── */}
      {isAdmin && showAdmin && <AdminConfigPanel channels={channels} onSaved={load} />}

      {prefsError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-xs text-red-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{prefsError}</span>
          <button onClick={() => setPrefsError(null)} className="ml-auto text-red-300/70 hover:text-red-200 underline">Dismiss</button>
        </div>
      )}

      {/* ─── Opt-in & filters ─────────────────────────────────────────── */}
      <OptInCard
        optedIn={optedIn}
        displayName={displayName}
        setDisplayName={setDisplayName}
        excludeLosses={excludeLosses}
        setExcludeLosses={setExcludeLosses}
        minTradeUsd={minTradeUsd}
        setMinTradeUsd={setMinTradeUsd}
        saving={saving}
        onSave={() => handleSavePrefs()}
        onToggle={(next) => handleSavePrefs(next)}
      />

      {/* ─── Channels grid ────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-pink-400" />
        </div>
      ) : channels.length === 0 ? (
        <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-12 text-center">
          <Megaphone className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-white font-medium">No channels configured</p>
        </div>
      ) : (
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Available channels</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {channels.map((ch) => <ChannelCard key={ch.id} channel={ch} />)}
          </div>
        </div>
      )}

      <p className="text-[10px] text-gray-600 text-center">
        Broadcasting respects your filters. Failures are silently dropped — never blocks your bot engines. Privacy: only your display name + tier are shared, never your userId or exchange identity.
      </p>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function AboutCard({ open, onToggle }) {
  return (
    <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 overflow-hidden">
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between hover:bg-dark-700/30 transition">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-pink-500/10 border border-pink-500/30 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-pink-400" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-white">What is this — and how does broadcasting work?</div>
            <div className="text-[11px] text-gray-500">Plain-English walkthrough · 20 seconds</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-dark-700/40 pt-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-pink-300 uppercase tracking-wider mb-1.5">What this is</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              haythix runs official Discord + Telegram channels organized by event type (Smart Trade fires, Grid Bot action, DCA accumulation, big wins, alerts). When you opt in, your real bot events get auto-posted to those channels so the wider community sees activity — and you build a reputation as an active trader.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-pink-300 uppercase tracking-wider mb-1.5">What's shown vs hidden</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              <span className="text-white">Shown:</span> your display name, tier badge, event type, symbol, price. <span className="text-white">Hidden:</span> your userId, email, exchange identity, account balance, position size, API keys. The webhook payload is built server-side and contains only those fields.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-pink-300 uppercase tracking-wider mb-1.5">Filters you control</div>
            <ul className="text-sm text-gray-300 leading-relaxed space-y-1 list-disc pl-5">
              <li><span className="text-white">Wins only</span> — stop-losses and negative-PnL closes won't be broadcast</li>
              <li><span className="text-white">Minimum trade size</span> — only broadcast trades where notional ≥ your floor</li>
              <li>Filters apply to ALL channels — you can't be selective per-channel today (planned)</li>
            </ul>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200/90 leading-relaxed">
              <span className="font-semibold">How to read this:</span> Broadcasting is a marketing/community channel, not financial advice. Other users' fires don't predict your outcomes. Webhook posts are best-effort — failures are silently dropped so they never block your bot engines.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function HeroStat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-dark-900/40 backdrop-blur-sm rounded-xl border border-dark-700/40 p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent || 'text-gray-400'}`} />
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
      </div>
      <p className={`text-xl font-bold font-mono ${accent || 'text-white'} truncate`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function OptInCard({ optedIn, displayName, setDisplayName, excludeLosses, setExcludeLosses, minTradeUsd, setMinTradeUsd, saving, onSave, onToggle }) {
  return (
    <div className={`rounded-2xl border p-5 transition-all ${optedIn ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-dark-800/40 border-dark-700/50'}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3 flex-1 min-w-[260px]">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${optedIn ? 'bg-emerald-500/15 text-emerald-400' : 'bg-dark-700 text-gray-500'}`}>
            {optedIn ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
          </div>
          <div>
            <p className="text-base font-bold text-white">
              {optedIn ? 'You are broadcasting' : 'Broadcasting is OFF'}
            </p>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed max-w-prose">
              {optedIn
                ? 'When your bots fire, opted-in events are auto-posted to haythix-owned Discord + Telegram channels below. Filters apply.'
                : 'Opt in to publish your real bot fires to the haythix community. Other traders see your display name + tier — never your userId or exchange identity. You can filter what gets broadcast (e.g. wins only).'}
            </p>
          </div>
        </div>
        <button
          onClick={() => onToggle(!optedIn)}
          disabled={saving}
          className={`text-xs px-5 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-1.5 ${
            optedIn
              ? 'bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20'
              : 'bg-gradient-to-r from-emerald-500 to-teal-400 text-dark-900 hover:from-emerald-400 hover:to-teal-300 shadow-lg shadow-emerald-500/20'
          } disabled:opacity-50`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : optedIn ? <><ShieldOff className="w-3 h-3" /> Opt out</> : <><Shield className="w-3 h-3" /> Opt in</>}
        </button>
      </div>

      {optedIn && (
        <div className="space-y-3 border-t border-dark-700/40 pt-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Display name (shown in broadcasts)</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value.slice(0, 32))}
              placeholder="haythix Founder"
              maxLength={32}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm focus:border-emerald-500/40 focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-start gap-2 cursor-pointer bg-dark-700/40 rounded-lg p-3 border border-dark-700/50 text-xs">
              <input
                type="checkbox"
                checked={excludeLosses}
                onChange={(e) => setExcludeLosses(e.target.checked)}
                className="mt-0.5 rounded"
              />
              <span>
                <span className="font-medium text-white">Wins only</span>
                <span className="block text-[10px] text-gray-500 mt-0.5">
                  Stop-losses and negative-P&L closes won't be broadcast
                </span>
              </span>
            </label>
            <div className="bg-dark-700/40 rounded-lg p-3 border border-dark-700/50">
              <label className="text-xs text-white font-medium block mb-1">Minimum trade size (USD)</label>
              <input
                type="number"
                min="0"
                step="any"
                value={minTradeUsd}
                onChange={(e) => setMinTradeUsd(e.target.value)}
                className="w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-xs font-mono"
              />
              <p className="text-[10px] text-gray-500 mt-1">Only broadcast trades where notional ≥ this amount. 0 = no minimum.</p>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={onSave}
              disabled={saving}
              className="text-xs px-4 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Save filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChannelCard({ channel }) {
  const Icon = CHANNEL_ICON[channel.id] || Hash;
  const accent = CHANNEL_ACCENT[channel.id] || 'text-gray-400 border-gray-500/30 bg-gray-500/5';
  return (
    <div className={`rounded-2xl border p-4 ${channel.configured ? 'bg-dark-800/40 border-dark-700/50' : 'bg-dark-800/30 border-dark-700/30 opacity-75'}`}>
      <div className="flex items-start gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${accent}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <p className="text-sm font-bold text-white">
              <span className="text-gray-500 font-mono">#</span>{channel.id}
            </p>
            {channel.configured ? (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                LIVE
              </span>
            ) : (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">
                COMING SOON
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-400 leading-relaxed mt-0.5">{channel.description}</p>
        </div>
        <span className="text-2xl flex-shrink-0">{channel.emoji}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3 text-[10px]">
        <div className={`rounded p-2 flex items-center gap-1.5 ${channel.hasDiscord ? 'bg-emerald-500/5 text-emerald-400' : 'bg-dark-700/30 text-gray-600'}`}>
          {channel.hasDiscord ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
          <span className="font-mono">Discord</span>
        </div>
        <div className={`rounded p-2 flex items-center gap-1.5 ${channel.hasTelegram ? 'bg-emerald-500/5 text-emerald-400' : 'bg-dark-700/30 text-gray-600'}`}>
          {channel.hasTelegram ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
          <span className="font-mono">Telegram</span>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {channel.matchEventTypes?.slice(0, 4).map((t) => (
          <span key={t} className="text-[9px] font-mono bg-dark-700/40 text-gray-400 px-1.5 py-0.5 rounded">{t}</span>
        ))}
        {channel.matchEventTypes?.length > 4 && (
          <span className="text-[9px] text-gray-600">+{channel.matchEventTypes.length - 4}</span>
        )}
      </div>
      {channel.minRealizedPnl != null && (
        <p className="text-[10px] text-yellow-400/80 mt-2 flex items-center gap-1">
          <Trophy className="w-2.5 h-2.5" /> Only events with realized P&L ≥ ${channel.minRealizedPnl}
        </p>
      )}
    </div>
  );
}

// Show only the host + last 4 path-chars of a webhook URL by default.
// Webhook URLs are SECRETS (anyone with the URL can post to your channel)
// so we don't want them lying around in plaintext on the admin page.
function maskUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const tail = u.pathname.slice(-4) || '';
    return `${u.protocol}//${u.host}/…${tail}`;
  } catch { return '••••••'; }
}

function AdminConfigPanel({ channels, onSaved }) {
  const [editing, setEditing] = useState(null); // channel id
  const [discordUrl, setDiscordUrl] = useState('');
  const [telegramUrl, setTelegramUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({});
  const [revealed, setRevealed] = useState({}); // channelId → true once user clicked Reveal
  const [adminError, setAdminError] = useState(null);

  useEffect(() => {
    getBroadcastAdminConfig()
      .then((r) => setConfig(r?.data?.config || {}))
      .catch(() => {});
  }, [editing]);

  function startEdit(ch) {
    setEditing(ch.id);
    const c = config[ch.id] || {};
    setDiscordUrl(c.discordUrl || '');
    setTelegramUrl(c.telegramUrl || '');
  }

  async function handleSave() {
    setSaving(true);
    setAdminError(null);
    try {
      await setBroadcastAdminConfig({
        channelId: editing,
        discordUrl,
        telegramUrl,
      });
      setEditing(null);
      await onSaved();
    } catch (err) {
      // Inline banner instead of blocking alert().
      setAdminError(err?.message || 'Could not save channel URLs. Please try again.');
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-amber-500/5 border border-amber-500/30 rounded-2xl p-5 space-y-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-300/90">
          <p className="font-bold text-amber-400">Admin: configure channel webhook URLs</p>
          <p className="mt-0.5 text-gray-300">Set the Discord and/or Telegram webhook URLs that haythix events broadcast to. Empty URL = channel inactive for that platform.</p>
          <p className="mt-1 text-gray-400">URLs are masked by default — click <span className="text-amber-400">Reveal</span> only when needed. Anyone with the URL can post to the channel.</p>
        </div>
      </div>

      {adminError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 text-[11px] text-red-300 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{adminError}</span>
          <button onClick={() => setAdminError(null)} className="ml-auto text-red-300/70 hover:text-red-200 underline">Dismiss</button>
        </div>
      )}

      <div className="space-y-2">
        {channels.map((ch) => (
          <div key={ch.id} className="bg-dark-800/40 rounded-lg p-3 border border-dark-700/50">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-sm font-mono text-white">#{ch.id}</p>
              {editing !== ch.id && (
                <button onClick={() => startEdit(ch)} className="text-[10px] px-2 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20">
                  Edit URLs
                </button>
              )}
            </div>
            {editing === ch.id ? (
              <div className="space-y-2 mt-2">
                <div>
                  <label className="text-[10px] text-gray-400 mb-1 block">Discord webhook URL</label>
                  <input
                    value={discordUrl}
                    onChange={(e) => setDiscordUrl(e.target.value)}
                    placeholder="https://discord.com/api/webhooks/…"
                    className="w-full bg-dark-700 border border-dark-600 rounded p-1.5 text-white text-[11px] font-mono"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 mb-1 block">Telegram bot URL (with chat_id)</label>
                  <input
                    value={telegramUrl}
                    onChange={(e) => setTelegramUrl(e.target.value)}
                    placeholder="https://api.telegram.org/bot…/sendMessage?chat_id=…"
                    className="w-full bg-dark-700 border border-dark-600 rounded p-1.5 text-white text-[11px] font-mono"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditing(null)} className="text-[10px] px-3 py-1.5 rounded border border-dark-600 text-gray-300 hover:bg-dark-700">
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="text-[10px] px-3 py-1.5 rounded bg-amber-500 text-dark-900 font-semibold hover:bg-amber-400 disabled:opacity-50 flex items-center gap-1"
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-[10px] text-gray-500 font-mono space-y-1 mt-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 w-16 flex-shrink-0">Discord:</span>
                  {config[ch.id]?.discordUrl ? (
                    revealed[`${ch.id}:d`] ? (
                      <span className="truncate text-gray-300">{config[ch.id].discordUrl}</span>
                    ) : (
                      <>
                        <span className="text-gray-400">{maskUrl(config[ch.id].discordUrl)}</span>
                        <button onClick={() => setRevealed(r => ({ ...r, [`${ch.id}:d`]: true }))}
                          className="ml-auto text-amber-400 hover:text-amber-300 underline">Reveal</button>
                      </>
                    )
                  ) : <span className="text-gray-700 italic">(not set)</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 w-16 flex-shrink-0">Telegram:</span>
                  {config[ch.id]?.telegramUrl ? (
                    revealed[`${ch.id}:t`] ? (
                      <span className="truncate text-gray-300">{config[ch.id].telegramUrl}</span>
                    ) : (
                      <>
                        <span className="text-gray-400">{maskUrl(config[ch.id].telegramUrl)}</span>
                        <button onClick={() => setRevealed(r => ({ ...r, [`${ch.id}:t`]: true }))}
                          className="ml-auto text-amber-400 hover:text-amber-300 underline">Reveal</button>
                      </>
                    )
                  ) : <span className="text-gray-700 italic">(not set)</span>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
